import { startCase, uniqueId } from 'lodash';

import { createMockWorkflowAction, createMockWorkflowRun } from 'dashboard/lib/stubs/workflow';
import { generateWorkflowRunId } from 'dashboard/lib/stubs/workflow-run';
import { EMPTY_LOGIC_BLOCK_OR_V2, LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { Verification } from 'dashboard/models/verification';
import {
  WorkflowRunStepAction,
  WorkflowRunStepActionAttributes,
  WorkflowRunStepConditional,
  WorkflowRunStepConditionalAttributes,
  WorkflowRunStepLoop,
  WorkflowRunStepLoopAttributes,
  WorkflowRunStepModule,
  WorkflowRunStepModuleAttributes,
  WorkflowRunStepParallel,
  WorkflowRunStepParallelAttributes,
  WorkflowRunStepRelationships,
  WorkflowRunStepStatus,
} from 'dashboard/models/workflow-step';
import {
  AllWorkflowAction,
  FakeWorkflowStepType,
  NullWorkflowAction,
  NullWorkflowStepActionKey,
  WorkflowAction,
  WorkflowActionCreateCaseV1,
  WorkflowActionEvaluateCodeV2,
  WorkflowActionFetchPersonaObjectV1,
  WorkflowActionFromConfig,
  WorkflowConditional,
  WorkflowElse,
  WorkflowRunStepError,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepLoop,
  WorkflowStepLoopModeType,
  WorkflowStepLoopType,
  WorkflowStepMapOutput,
  WorkflowStepModule,
  WorkflowStepOnErrorAction,
  WorkflowStepParallel,
  WorkflowStepPlaceholder,
  WorkflowStepType,
  WorkflowStepWait,
  WorkflowWatchedEvent,
} from 'dashboard/models/workflow-version';
import { EventName } from 'lib/constants/events';
import { FieldSchemaType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchema, JsonSchemaArray } from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

export const generateWorkflowStepId = () => uniqueId('wfs_');

export function createMockWorkflowRunStepAction(
  attributes?: Partial<WorkflowRunStepActionAttributes>,
  relationships?: Partial<WorkflowRunStepRelationships>,
  id?: string,
  runError?: WorkflowRunStepError,
): WorkflowRunStepAction {
  const configStepToken = generateWorkflowToken();
  return {
    attributes: {
      'config-step-token': configStepToken,
      status: WorkflowRunStepStatus.Completed,
      'known-objects-map': {},
      'known-variables-map': {},
      'generated-variable': null,
      'step-error': runError,
      'created-at': '2020-02-15T09:31:50.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      'total-attempts': 1,
      ...attributes,
    },
    id: id || generateWorkflowStepId(),
    relationships: {
      'known-objects': { data: [] },
      'workflow-run': {
        data: {
          type: ApiModel.WorkflowRun,
          id: generateWorkflowRunId(),
        },
      },
      ...relationships,
    },
    type: ApiModel.WorkflowRunStepAction,
  };
}

export function createMockWorkflowRunStepConditional(
  attributes?: Partial<WorkflowRunStepConditionalAttributes>,
  relationships?: Partial<WorkflowRunStepRelationships>,
  id?: string,
): WorkflowRunStepConditional {
  const configStepToken = generateWorkflowToken();
  return {
    attributes: {
      'config-step-token': configStepToken,
      'completed-at': '2020-02-15T09:31:58.000Z',
      'created-at': '2020-02-15T09:31:50.000Z',
      status: WorkflowRunStepStatus.Completed,
      'chosen-conditional-index': 0,
      'chosen-or-indices': [],
      'known-objects-map': {},
      'known-variables-map': {},
      'generated-variable': null,
      'updated-at': '2020-02-15T09:31:58.000Z',
      'total-attempts': 1,
      ...attributes,
    },
    id: id || generateWorkflowStepId(),
    relationships: {
      'known-objects': { data: [] },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'workflow-run': undefined,
      ...relationships,
    },
    type: ApiModel.WorkflowRunStepConditional,
  };
}

export function createMockWorkflowRunStepLoop(
  attributes?: Partial<WorkflowRunStepLoopAttributes>,
  relationships?: Partial<WorkflowRunStepRelationships>,
  id?: string,
): WorkflowRunStepLoop {
  return {
    attributes: {
      'config-step-token': generateWorkflowToken(),
      status: WorkflowRunStepStatus.Completed,
      'known-objects-map': {},
      'known-variables-map': {},
      'generated-variable': null,
      'errored-cycles': [],
      'current-cycle': 0,
      'cycle-to-step-tokens': {},
      'resolved-input-array': [],
      'created-at': '2020-02-15T09:31:50.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      'total-attempts': 1,
      settings: {
        type: WorkflowStepLoopType.ForEach,
        'array-path': 'trigger.fields.files',
        mode: WorkflowStepLoopModeType.SEQUENTIAL,
      },
      ...attributes,
    },
    id: id || generateWorkflowStepId(),
    relationships: {
      'known-objects': { data: [] },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'workflow-run': undefined,
      ...relationships,
    },
    type: ApiModel.WorkflowRunStepLoop,
  };
}

export function createMockWorkflowRunStepModule(
  attributes?: Partial<WorkflowRunStepModuleAttributes>,
  relationships?: Partial<WorkflowRunStepRelationships>,
  id?: string,
): WorkflowRunStepModule {
  return {
    attributes: {
      'config-step-token': generateWorkflowToken(),
      status: WorkflowRunStepStatus.Completed,
      'known-objects-map': {},
      'known-variables-map': {},
      'generated-variable': null,
      'created-at': '2020-02-15T09:31:50.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      'total-attempts': 1,
      ...attributes,
    },
    id: id || generateWorkflowStepId(),
    relationships: {
      'known-objects': { data: [] },
      'workflow-run': { data: createMockWorkflowRun() },
      ...relationships,
    },
    type: ApiModel.WorkflowRunStepModule,
  };
}

export function createMockWorkflowRunParallel(
  attributes?: Partial<WorkflowRunStepParallelAttributes>,
  relationships?: Partial<WorkflowRunStepRelationships>,
  id?: string,
): WorkflowRunStepParallel {
  const configStepToken = generateWorkflowToken();
  return {
    attributes: {
      'config-step-token': configStepToken,
      status: WorkflowRunStepStatus.Completed,
      'known-objects-map': {},
      'known-variables-map': {},
      'generated-variable': null,
      'created-at': '2020-02-15T09:31:50.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      'total-attempts': 1,
      ...attributes,
    },
    id: id || generateWorkflowStepId(),
    relationships: {
      'known-objects': { data: [] },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'workflow-run': undefined,
      ...relationships,
    },
    type: ApiModel.WorkflowRunStepParallel,
  };
}

type MockActionParams = Extract<
  Omit<WorkflowAction, 'version'>,
  {
    key: WorkflowAction['key'];
    params: WorkflowAction['params'];
  }
> & {
  token?: WorkflowStep['token'];
  version?: WorkflowAction['version'];
  continueOnError?: boolean;
};

type ConditionalRoute = {
  steps?: WorkflowStep[];
  rules?: LogicBlockOrV2;
};

export const mockAction = ({
  key,
  params,
  token = uniqueId(),
  version = 1,
  continueOnError = false,
}: MockActionParams): WorkflowStepAction => {
  const action = {
    key,
    params,
    version: version,
  } as WorkflowAction;
  return {
    name: startCase(key),
    type: WorkflowStepType.Action,
    token,
    action,
    ...(continueOnError ? { 'on-error': WorkflowStepOnErrorAction.Continue } : {}),
  };
};

export const mockActionFromConfig = ({
  key,
  params = {},
}: {
  key: string;
  params: WorkflowActionFromConfig['params'];
}): WorkflowActionFromConfig => {
  return {
    key,
    version: 1,
    params,
  };
};

export const mockPlaceholder = ({
  token = uniqueId(),
}: { token?: string } = {}): WorkflowStepPlaceholder => {
  return {
    name: 'Placeholder',
    token,
    type: WorkflowStepType.Placeholder,
  };
};

export const mockWait = ({
  watchedEvents = null,
  delay = 3600,
  token = uniqueId(),
}: {
  watchedEvents?: WorkflowWatchedEvent[] | null;
  delay?: number;
  token?: string;
} = {}): WorkflowStepWait => {
  // e.g. 'case.resolved' => 'Case Resolved'
  const names = watchedEvents
    ? watchedEvents.map((event) => startCase(event['event-name']))
    : ['Time'];

  return {
    name: `Wait For ${names.join('&')}`,
    type: WorkflowStepType.Wait,
    token,
    'watched-events': watchedEvents,
    'wait-period-seconds': delay,
  };
};

export const mockForEachLoop = (
  branch: WorkflowStep[] = [],
  arrayPath = '',
  inputArraySchema?: JsonSchemaArray,
): WorkflowStepLoop => {
  return {
    name: 'Loop Step',
    type: WorkflowStepType.Loop,
    token: generateWorkflowToken(),
    branch,
    settings: {
      type: WorkflowStepLoopType.ForEach,
      'array-path': arrayPath,
      'input-array-schema': inputArraySchema,
      mode: WorkflowStepLoopModeType.SEQUENTIAL,
    },
  };
};

export const mockWhileLoop = (
  branch: WorkflowStep[] = [],
  conditional: WorkflowConditional = {
    name: '',
    token: generateWorkflowToken(),
    rules: EMPTY_LOGIC_BLOCK_OR_V2,
  },
  maxIterations = 3,
): WorkflowStepLoop => {
  return {
    name: 'Loop Step',
    type: WorkflowStepType.Loop,
    token: generateWorkflowToken(),
    branch,
    settings: {
      type: WorkflowStepLoopType.While,
      conditional: conditional,
      'max-iterations': maxIterations,
    },
  };
};

export const mockMapLoop = (
  branch: WorkflowStep[] = [],
  arrayPath = '',
  itemPath = '',
  outputStepToken = generateWorkflowToken(),
  itemPathSchema?: JsonSchema,
): WorkflowStepLoop => {
  return {
    name: 'Loop step',
    type: WorkflowStepType.Loop,
    token: generateWorkflowToken(),
    branch: [...branch, mockMapOutput(outputStepToken)],
    settings: {
      type: WorkflowStepLoopType.Map,
      'array-path': arrayPath,
      'item-path': itemPath,
      'output-step-token': outputStepToken,
      'output-item-schema': itemPathSchema,
      mode: WorkflowStepLoopModeType.SEQUENTIAL,
    },
  };
};

export const mockMapLoopWithoutOutputStep = (
  branch: WorkflowStep[] = [],
  arrayPath = '',
  itemPath = '',
  token = generateWorkflowToken(),
): WorkflowStepLoop => {
  return {
    name: 'Loop step',
    type: WorkflowStepType.Loop,
    token,
    branch: [...branch],
    settings: {
      type: WorkflowStepLoopType.Map,
      'array-path': arrayPath,
      'item-path': itemPath,
      mode: WorkflowStepLoopModeType.SEQUENTIAL,
    },
  };
};

export const mockMapOutput = (token = generateWorkflowToken()): WorkflowStepMapOutput => {
  return {
    name: 'Array Map Output',
    token,
    type: FakeWorkflowStepType.MapOutput,
  };
};

export const mockParallel = (branches: WorkflowStep[][]): WorkflowStepParallel => {
  return {
    name: 'Parallel Step',
    type: WorkflowStepType.Parallel,
    token: uniqueId(),
    branches,
  };
};

export const mockConditional = (routes: ConditionalRoute[]): WorkflowStepConditional => {
  const branches: WorkflowStep[][] = [];
  const conditionals: (WorkflowConditional | WorkflowElse)[] = [];
  routes.forEach(({ steps = [], rules }) => {
    branches.push(steps);
    const conditional = rules
      ? {
          name: `Route ${conditionals.length + 1}`,
          rules,
          token: uniqueId(),
        }
      : {
          name: 'Else' as WorkflowElse['name'],
          rules: true as WorkflowElse['rules'],
          token: uniqueId(),
        };
    conditionals.push(conditional);
  });
  return {
    name: 'Conditional Step',
    type: WorkflowStepType.Conditional,
    token: uniqueId(),
    branches,
    conditionals,
  };
};

export const mockCaseCreateAndWait = (caseTemplateToken = 'ctmpl_123') => {
  const action = mockAction({
    key: WorkflowStepActionKey.CreateCase,
    params: {
      'case-objects': ['trigger'],
      'case-template-token': caseTemplateToken,
      'case-fields': {},
    },
  });
  const wait = mockWait({
    watchedEvents: [
      {
        'event-name': EventName.CaseResolved,
        'event-object-step-token': action.token,
      },
    ],
  });
  return [action, wait];
};

export const mockCreateInquiry = (params = {}) => {
  return mockAction({
    params: {
      'template-id': 'itmpl_123',
      'theme-id': 'the_123',
      'theme-set-id': 'theSet_123',
      account: 'act_123',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redirect-uri': null,
      ...params,
    },
    key: WorkflowStepActionKey.CreateInquiry,
  });
};

export const mockCreateCase = (
  paramsOverwrite: Partial<WorkflowActionCreateCaseV1['params']> = {},
) => {
  return mockAction({
    params: {
      'case-template-token': 'ctmpl_123',
      'case-objects': [],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'case-fields': null,
      ...paramsOverwrite,
    },
    key: WorkflowStepActionKey.CreateCase,
  });
};

export const mockRunVerification = (verificationType: Verification['type'], vtvId?: string) => {
  return mockAction({
    params: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      object: null,
      'verification-template-version-id': vtvId || 'vtv_123',
      'verification-fields': {},
      'verification-type': verificationType,
    },
    key: WorkflowStepActionKey.RunVerificationGeneric,
  });
};

export const mockFetchPersonaObject = (
  paramsOverwrite: Partial<WorkflowActionFetchPersonaObjectV1['params']> = {},
) => {
  return mockAction({
    params: {
      'object-type': ApiModel.Account,
      id: 'act_123',
      ...paramsOverwrite,
    },
    key: WorkflowStepActionKey.FetchPersonaObject,
  });
};

export const createMockStepForAction = <T extends AllWorkflowAction>({
  action,
  token = generateWorkflowToken(),
  name = 'Some Workflow Action Step',
}: {
  action: T;
  token?: string;
  name?: string;
}): WorkflowStepAction<T> => ({
  type: WorkflowStepType.Action,
  token: token,
  name: name,
  action: action,
});

type WorkflowActionMap = {
  [K in AllWorkflowAction['key']]: Extract<AllWorkflowAction, { key: K }>;
};

export const createMockWorkflowActionStep = <K extends AllWorkflowAction['key']>({
  key,
  params,
  version,
  name,
  token,
}: {
  key: K;
  params?: WorkflowActionMap[K]['params'];
  version?: WorkflowActionMap[K]['version'];
  name?: string;
  token?: string;
}): WorkflowStepAction => ({
  token: token || uniqueId('step_'),
  type: WorkflowStepType.Action,
  name: name || 'Action',
  action: createMockWorkflowAction({ key, params, version }),
});

export const createMockNullAction = ({
  token = generateWorkflowToken(),
  name = 'Some Workflow Action Step',
}: {
  token?: string;
  name?: string;
}): WorkflowStepAction =>
  createMockStepForAction({
    action: { key: NullWorkflowStepActionKey.NullKey } as NullWorkflowAction,
    token: token,
    name: name,
  });

export const mockEvaluateCode = (
  paramsOverwrite: Partial<WorkflowActionEvaluateCodeV2['params']> = {},
  token: string = uniqueId(),
): WorkflowStepAction =>
  createMockStepForAction({
    token,
    name: 'Evaluate code',
    action: {
      params: {
        'input-args': {},
        'return-schema': {
          type: FieldSchemaType.Json,
          key: 'result',
          label: 'Result',
          config: {
            'json-schema': JSON.stringify({ type: null }),
          },
        },
        'serialized-code': btoa('console.log("hi")'),
        ...paramsOverwrite,
      },
      key: WorkflowStepActionKey.EvaluateCode,
      version: 2,
    },
  });

export const mockModuleStep = (
  attributes: Partial<WorkflowStepModule> = {},
): WorkflowStepModule => {
  return {
    type: WorkflowStepType.Module,
    token: uniqueId(),
    name: 'My module',
    'input-mappings': [],
    'child-workflow-block-id': '',
    ...attributes,
  };
};
